// Install-snippet builder — typed extraction from the pixels drawer.
//
// Renders the canonical snippet generator (shared/js/install-snippets.js,
// byte-locked to shared/src/install_snippets.rs via the goldens in
// shared/testdata/install_snippets/) behind a method segmented control and
// per-parameter checkboxes driven by the shared install catalog
// (INSTALL_PARAMS, emitted by gen-ts from shared::install_catalog).

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { INSTALL_PARAMS, SGTM_PARAMS, DEFAULT_PIXEL_HOST } from '../api/types.gen';
import type { ParamSpec, Pixel } from '../api/types.gen';
import { MultiSelect } from '@/components/multi-select';
import type { MultiSelectOption } from '@/components/multi-select';
import '@shared/js/install-snippets.js';

type SnippetMethod = 'gtm' | 'head' | 'manual';
type SnippetEvent = 'Pageview' | 'Purchase';
type MethodTab = SnippetMethod | 'sgtm';

// Narrow local view of the UMD global — the generator is ES5/UMD on purpose
// (embedded verbatim by the pixel-worker /install page), so there is no
// module export to type; the side-effect import above installs it.
interface SnippetGenerator {
  render(
    method: SnippetMethod,
    opts: {
      host: string;
      pixelId: string;
      event?: SnippetEvent;
      enabled: Record<string, boolean>;
      catalog: ParamSpec[];
    },
  ): string;
}

const gen = (window as unknown as { SamInstallSnippets: SnippetGenerator }).SamInstallSnippets;

const METHOD_TABS: Array<{ id: MethodTab; label: string }> = [
  { id: 'gtm', label: 'Google Tag Manager' },
  { id: 'head', label: 'Website <head>' },
  { id: 'manual', label: 'SPA / Manual JS' },
  { id: 'sgtm', label: 'Server-side GTM' },
];

const GROUPS: Array<{ id: string; label: string }> = [
  { id: 'attribution', label: 'Attribution' },
  { id: 'transaction', label: 'Transaction' },
  { id: 'privacy', label: 'Privacy' },
];

// Server-side funnel default covering a catalog key, if any. A covered
// parameter starts unchecked — the pixel registry already supplies the value,
// so putting it in the page snippet would be redundant (and can go stale).
function serverDefaultFor(pixel: Pixel, key: string): string | null {
  if (key === 'production_id') return pixel.default_production_id;
  if (key === 'venue_id') return pixel.default_venue_id;
  return null;
}

function initialEnabled(pixel: Pixel): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const p of INSTALL_PARAMS) {
    if (p.key === 'pixel_id') continue; // always on, never a checkbox
    if (p.required_for.length > 0) {
      out[p.key] = true;
    } else if (serverDefaultFor(pixel, p.key) != null && serverDefaultFor(pixel, p.key) !== '') {
      out[p.key] = false;
    } else {
      out[p.key] = p.default_on;
    }
  }
  return out;
}

// The parameter picker's rows: every catalog key but `pixel_id`, which is
// always sent. A required key is locked on; a key the pixel's server-side
// default covers says so.
function installParamOptions(pixel: Pixel): MultiSelectOption[] {
  const groupLabel = (id: string) => GROUPS.find((g) => g.id === id)?.label ?? id;
  return INSTALL_PARAMS.filter((p) => p.key !== 'pixel_id').map((p) => {
    const serverDefault = serverDefaultFor(pixel, p.key);
    return {
      id: p.key,
      label: p.label,
      sublabel: serverDefault
        ? `${groupLabel(p.group)} · server default: ${serverDefault}`
        : groupLabel(p.group),
      meta: p.required_for.length > 0 ? 'required' : undefined,
      locked: p.required_for.length > 0,
    };
  });
}

// The renderer reads a record over every key, so the picker's array maps back
// to exactly the shape `initialEnabled` builds.
function enabledFromKeys(keys: readonly string[]): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const p of INSTALL_PARAMS) {
    if (p.key === 'pixel_id') continue;
    out[p.key] = p.required_for.length > 0 || keys.includes(p.key);
  }
  return out;
}

// ── Server-side GTM guide ───────────────────────────────────────────────────
// Setup steps + the template-field reference, driven by the shared
// SGTM_PARAMS catalog (gen-ts from shared::sgtm_params) so UI and template
// can't drift. Lives inside the builder's "Server-side GTM" tab.

const SGTM_GROUP_LABELS: Record<string, string> = {
  required: 'Required',
  routing: 'Event routing',
  commerce: 'Commerce fields',
  sam: 'SAM funnel fields',
  identity: 'Identity',
  pii: 'PII',
  consent: 'Consent',
  advanced: 'Advanced',
};

// Catalog order, first-appearance grouping — the emitted array is already in
// display order, so no hardcoded group list to drift.
function sgtmGroups(): Array<{ id: string; label: string; params: ParamSpec[] }> {
  const out: Array<{ id: string; label: string; params: ParamSpec[] }> = [];
  for (const p of SGTM_PARAMS) {
    let g = out.find((x) => x.id === p.group);
    if (!g) {
      g = { id: p.group, label: SGTM_GROUP_LABELS[p.group] ?? p.group, params: [] };
      out.push(g);
    }
    g.params.push(p);
  }
  return out;
}

function SgtmStep({ num, title, children }: { num: number; title: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 12 }}>
      <span
        className="mono"
        style={{
          flexShrink: 0, width: 20, height: 20, borderRadius: '50%',
          border: '1px solid var(--accent)', color: 'var(--accent)',
          fontSize: 10.5, fontWeight: 700,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {num}
      </span>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 2 }}>{title}</div>
        <div style={{ fontSize: 11.5, lineHeight: 1.55, color: 'var(--ink-1)' }}>{children}</div>
      </div>
    </div>
  );
}

export function SgtmGuide({ pixel }: { pixel: Pixel }) {
  // Defensive: the server-key fields land with the API release; a Pixel
  // from an older API simply shows "not minted yet" guidance.
  const hasServerKey = !!(pixel as { has_server_key?: boolean }).has_server_key;
  const groups = useMemo(sgtmGroups, []);
  return (
    <div>
      <div className="muted" style={{ fontSize: 11.5, lineHeight: 1.55, marginBottom: 12 }}>
        Forward events from a GTM <b>server container</b> (e.g. an existing GA4 sGTM setup) to SAM
        server-side — no page changes needed. The tag authenticates with a per-pixel server key and
        posts to <code className="mono">/v1/collect/server</code>.
      </div>

      <SgtmStep num={1} title="Mint a server key">
        In this pixel's drawer, open <b>Provider integrations → Server key (sGTM)</b> (unlock the
        section first) and click <b>Mint key</b>.{' '}
        {hasServerKey
          ? <span style={{ color: 'var(--good)' }}>This pixel already has a server key set.</span>
          : <span>This pixel has no server key yet.</span>}{' '}
        The key is shown <b>once</b> — copy it straight into the server container.
      </SgtmStep>

      <SgtmStep num={2} title="Get the tag template">
        <a href="/sgtm/sam-collect.tpl" download className="mono" style={{ color: 'var(--accent)' }}>
          Download sam-collect.tpl
        </a>{' '}
        — or import it from the public template gallery repo.
      </SgtmStep>

      <SgtmStep num={3} title="Import into the server container">
        In your GTM <b>server</b> container: <b>Templates → Tag Templates → New → ⋮ → Import</b>,
        pick <code className="mono">sam-collect.tpl</code>, save, then create a tag from the new
        template and trigger it on your GA4 events.
      </SgtmStep>

      <SgtmStep num={4} title="Configure the tag fields">
        Set <code className="mono">pixelId</code> to <code className="mono">{pixel.id || '<pixel_id>'}</code> and
        paste the server key from step 1 (store it as a container constant). Everything else has
        working defaults:
      </SgtmStep>

      <div
        className="tbl-wrap"
        style={{ margin: '0 0 12px 30px', border: '1px solid var(--line)', borderRadius: 'var(--r-sm,4px)', overflowX: 'auto' }}
      >
        <table className="tbl" style={{ margin: 0 }}>
          <thead>
            <tr>
              <th style={{ minWidth: 130 }}>Field</th>
              <th style={{ minWidth: 120 }}>Type</th>
              <th style={{ minWidth: 110 }}>Default</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((g) => (
              <Fragment key={g.id}>
                <tr style={{ cursor: 'default' }}>
                  <td
                    colSpan={4}
                    className="muted mono"
                    style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', background: 'var(--bg-sunk)', padding: '5px 10px' }}
                  >
                    {g.label}
                  </td>
                </tr>
                {g.params.map((p) => (
                  <tr key={p.key} style={{ cursor: 'default' }}>
                    <td className="mono" style={{ fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' }}>{p.key}</td>
                    <td className="mono" style={{ fontSize: 10.5, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>{p.shape}</td>
                    <td className="mono" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{p.example || '—'}</td>
                    <td style={{ whiteSpace: 'normal', fontSize: 11.5, lineHeight: 1.35, color: 'var(--ink-1)' }}>{p.description}</td>
                  </tr>
                ))}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <SgtmStep num={5} title="Verify">
        Use GTM <b>Preview</b> on the server container and fire a test event — the tag's log should
        show a 2xx from the endpoint. The pixel's <code className="mono">/install</code> debugger
        shows the events with channel <code className="mono">sgtm</code>, and with a{' '}
        <code className="mono">testEventCode</code> set, server events appear in Meta&nbsp;
        <b>Events Manager → Test Events</b>. Remove the test code once verified.
      </SgtmStep>
    </div>
  );
}

export function InstallSnippetBuilder({ pixel }: { pixel: Pixel }) {
  const [method, setMethod] = useState<MethodTab>('gtm');
  const [event, setEvent] = useState<SnippetEvent>('Purchase');
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => initialEnabled(pixel));
  const [copied, setCopied] = useState(false);
  const paramOptions = useMemo(() => installParamOptions(pixel), [pixel]);

  // Re-derive checkbox state when the drawer switches to a different pixel.
  useEffect(() => {
    setEnabled(initialEnabled(pixel));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pixel.id]);

  const snippet = useMemo(() => {
    if (method === 'sgtm') return '';
    return gen.render(method, {
      host: DEFAULT_PIXEL_HOST,
      pixelId: pixel.id || '<pixel_id>',
      event,
      enabled,
      catalog: INSTALL_PARAMS,
    });
  }, [method, event, enabled, pixel.id]);

  const onCopy = () => {
    void navigator.clipboard?.writeText(snippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div style={{ marginBottom: 14 }}>
      {/* Method segmented control — same border-bottom-accent idiom as the
          old per-event tabs this builder replaced. */}
      <div
        className="pixel-method-tabs"
        style={{
          display: 'flex',
          gap: 0,
          border: '1px solid var(--line)',
          borderBottom: 'none',
          borderRadius: 'var(--r-sm,4px) var(--r-sm,4px) 0 0',
          background: 'var(--bg-elev)',
          overflow: 'hidden',
        }}
      >
        {METHOD_TABS.map((t) => {
          const active = t.id === method;
          return (
            <button
              key={t.id}
              onClick={() => setMethod(t.id)}
              style={{
                flex: '0 0 auto',
                padding: '7px 14px',
                background: active ? 'var(--bg-sunk)' : 'transparent',
                border: 'none',
                borderRight: '1px solid var(--line)',
                borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '.04em',
                fontWeight: active ? 700 : 500,
                color: active ? 'var(--ink)' : 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      <div
        style={{
          border: '1px solid var(--line)',
          borderRadius: '0 0 var(--r-sm,4px) var(--r-sm,4px)',
          padding: 12,
          background: 'var(--bg-elev)',
        }}
      >
        {method === 'sgtm' ? (
          <SgtmGuide pixel={pixel} />
        ) : (
          <>
            {/* The parameters the snippet sends. Required ones are locked on;
                the record shape the renderer reads is unchanged. */}
            <div style={{ marginBottom: 10 }}>
              <div
                className="muted mono"
                style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 4 }}
              >
                Parameters
              </div>
              <MultiSelect
                ariaLabel="Snippet parameters"
                order="alpha"
                value={paramOptions.filter((option) => enabled[option.id]).map((option) => option.id)}
                options={paramOptions}
                onChange={(keys) => setEnabled(enabledFromKeys(keys))}
                placeholder="No parameters"
                searchPlaceholder={`Search ${paramOptions.length} parameters…`}
                popWidth={420}
                size="sm"
              />
            </div>

            {/* Manual/SPA snippets carry an explicit sam('track', …) call, so the
                event matters; GTM and <head> embeds are event-agnostic loaders. */}
            {method === 'manual' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span
                  className="muted mono"
                  style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase' }}
                >
                  Event
                </span>
                {(['Purchase', 'Pageview'] as const).map((ev) => {
                  const active = ev === event;
                  return (
                    <button
                      key={ev}
                      className="btn btn-sm"
                      onClick={() => setEvent(ev)}
                      style={
                        active
                          ? { background: 'var(--bg-sunk)', borderColor: 'var(--accent)', fontWeight: 700 }
                          : {}
                      }
                    >
                      {ev}
                    </button>
                  );
                })}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '2px 0 6px',
              }}
            >
              <span
                className="muted mono"
                style={{ fontSize: 9.5, letterSpacing: '.1em', textTransform: 'uppercase' }}
              >
                Snippet · replace example values
              </span>
              <button className="btn btn-sm" onClick={onCopy}>
                {copied ? '✓ Copied' : 'Copy snippet'}
              </button>
            </div>
            <pre
              style={{
                background: 'var(--bg-sunk)',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-sm,4px)',
                padding: 12,
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                lineHeight: 1.5,
                color: 'var(--ink-1)',
                overflowX: 'auto',
                margin: 0,
                whiteSpace: 'pre',
              }}
            >
              {snippet}
            </pre>
          </>
        )}
      </div>
    </div>
  );
}
